import React, { memo, ReactElement, useMemo, lazy, ComponentType, ExoticComponent, useEffect } from 'react';
import { Route, RouteProps } from 'react-router-dom';
import { RouteContextProvider, useOptionalRouteMatch } from './RouteContext';
import { getRoutePath } from './utils';
import { useAppContext, appContext$ } from '@core/components/AppContextProvider';
import { HATAG } from '@business/constants/helparticle';
import { HelpArticleTags$ } from '@modules/dashboard/DashboardPage/DashboardRouter';
import ReactGA from 'react-ga4';
import { R } from '@core/utils/r';
import { useObservable } from '@core/utils/hooks/rxjs';
import { Session } from '@modules/auth/session';
import { CommonService } from '@business/services';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { TagToAnalyticsModuleMap } from '@business/entities/analytics/AnalyticsRequest';
import { AnalyticsService } from '@business/services/analytics_service';

export interface LazyRouteProps<T = any> extends RouteProps {
  absolutePath?: string | string[];
  guard?(): ReactElement | null;
  resolve?(): Promise<{ default: T }>;
  component?: ComponentType | ExoticComponent;
  spinner?: boolean;
  computedPath?: string;
  tags?: HATAG[];
}

export const LazyRoute = memo((props: LazyRouteProps) => {
  const { guard, computedPath, path: relativePath, absolutePath, component, resolve, ...rest } = props;
  const app = useAppContext();
  appContext$.next(app);
  HelpArticleTags$.next(props.tags || []);
  const content = props.guard ? props.guard() : null;
  const authUser = useObservable(Session.user$);
  const match = useOptionalRouteMatch();
  const settings = useSettings();

  const routePath = useMemo(() => {
    if (computedPath) return computedPath;

    return getRoutePath(absolutePath, relativePath as any, match?.path);
  }, [match, computedPath, absolutePath, relativePath]);

  useEffect(() => {
    try {
      const tenantName = R.get(settings, 'appName', '');
      const username = CommonService.getFullName(authUser);
      const allTags = props.tags || [];
      allTags.forEach(rawTag => {
        const moduleName = AnalyticsService.getModuleName(rawTag);
        if (!R.isEmpty(moduleName) && !R.isEmpty(tenantName)) {
          ReactGA.event('Visited Module', {
            category: 'Module Usage',
            label: moduleName,
            nonInteraction: true,
            tenant: tenantName,
            username: username,
            module: moduleName,
          });
        }
      });
    } catch (error) {
      console.error('GA4 Event Sending Error:', error);
    }
  }, [props.tags, authUser, settings]);

  const Component = useMemo(() => {
    if (component) return component;
    if (resolve) return lazy(resolve);
    return () => null;
  }, [component, resolve]);

  if (content) return content;

  return (
    <Route
      {...rest}
      path={routePath}
      render={({ match }) => (
        <RouteContextProvider match={match}>
          <Component />
        </RouteContextProvider>
      )}
    />
  );
});
